import React, { useEffect, useState } from 'react';
import { brandingService, BrandingProfile } from '../../../services/api';

/**
 * Per-organisation email branding. Tenants of this landlord see emails
 * "from Causeway" instead of "from Landlord Pro". Replies route to the
 * org's own inbox via Reply-To. Sender envelope stays on the platform's
 * verified domain so SPF/DKIM don't break — only the visible name + the
 * footer + reply-to change.
 */
const EMPTY: BrandingProfile = {
    orgName:      '',
    fromName:     '',
    replyToEmail: '',
    supportEmail: '',
    supportPhone: '',
    logoUrl:      '',
    logoS3Key:    '',
    brandColor:   '#10b981',
    footerText:   '',
    websiteUrl:   '',
    whatsappNumber:      '',
    whatsappConfirmed:   false,
    whatsappConfirmedAt: null,
};

export default function BrandingTab() {
    const [form, setForm]       = useState<BrandingProfile>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [savedAt, setSavedAt] = useState<string | null>(null);
    const [error, setError]     = useState<string | null>(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
    // Local DataURL preview shown immediately after a file is picked, so the
    // user can verify it BEFORE saving. After save+reload, the preview
    // switches back to the backend-generated presigned URL on form.logoUrl.
    const [logoLocalPreview, setLogoLocalPreview] = useState<string | null>(null);
    const [confirmingWa, setConfirmingWa] = useState(false);
    const [waError, setWaError] = useState<string | null>(null);
    // Tracks the number as last loaded/saved from the backend, so "Confirm"
    // can be disabled while the field has unsaved edits — confirming a
    // number that hasn't been saved yet would confirm the OLD stored value,
    // not what's currently typed.
    const [savedWhatsappNumber, setSavedWhatsappNumber] = useState('');

    /**
     * Resize the picked image down to email-friendly dimensions (max 240px on
     * the long side, height capped at 80px for header use) and re-encode as
     * WebP. The previous code uploaded the raw file which made the emailed
     * logo render as a scrambled / oversized blob in some clients. Resizing
     * to a tiny WebP also keeps the S3 object small (typically <10 KB) so
     * Brevo/SMTP fetches it in milliseconds.
     *
     * SVG can't be canvas-resized losslessly, so it's passed through unchanged.
     */
    const resizeLogoToWebP = (file: File): Promise<Blob> => new Promise((resolve, reject) => {
        if (file.type === 'image/svg+xml') {
            resolve(file);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const MAX_W = 240;
                const MAX_H = 80;
                const ratio = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
                const w = Math.max(1, Math.round(img.width * ratio));
                const h = Math.max(1, Math.round(img.height * ratio));
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject(new Error('Canvas 2D context unavailable')); return; }
                // Transparent background — preserves PNG transparency in the WebP output.
                ctx.clearRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null')),
                    'image/webp',
                    0.9
                );
            };
            img.onerror = () => reject(new Error('Image failed to load (corrupt or unsupported format)'));
            img.src = String(e.target?.result || '');
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(file);
    });

    const handleLogoFile = async (file: File) => {
        if (file.size > 5 * 1024 * 1024) {
            setLogoUploadError('Source image too large (max 5 MB before resize).');
            return;
        }
        if (!/^image\/(png|jpeg|svg\+xml|webp|gif)$/.test(file.type)) {
            setLogoUploadError('Use PNG, JPEG, SVG, WebP, or GIF.');
            return;
        }
        setLogoUploadError(null);
        setLogoUploading(true);
        try {
            // Step 1: resize+convert to WebP so the emailed logo always renders correctly.
            const optimised = await resizeLogoToWebP(file);
            const isSvg = file.type === 'image/svg+xml';
            const uploadType = isSvg ? 'image/svg+xml' : 'image/webp';
            const uploadName = isSvg ? file.name.replace(/[^a-zA-Z0-9._-]+/g, '_') : 'logo.webp';

            // Step 2: build a stable local preview from the OPTIMISED bytes so
            // the user sees exactly what will be uploaded. (The previous code
            // read the ORIGINAL file which sometimes never resolved before
            // the upload finished, leaving the preview blank.)
            const previewUrl = await new Promise<string>((resolve, reject) => {
                const r = new FileReader();
                r.onload = (ev) => resolve(String(ev.target?.result || ''));
                r.onerror = () => reject(new Error('Preview read failed'));
                r.readAsDataURL(optimised);
            });
            setLogoLocalPreview(previewUrl);

            // Step 3: presigned URL + S3 PUT for the OPTIMISED blob.
            const pres = await brandingService.getLogoPresignedUrl({ fileName: uploadName, fileType: uploadType });
            const { uploadUrl, s3Key } = pres.data?.data || ({} as any);
            if (!uploadUrl || !s3Key) throw new Error('Failed to get upload URL');

            const putRes = await fetch(uploadUrl, { method: 'PUT', body: optimised, headers: { 'Content-Type': uploadType } });
            if (!putRes.ok) throw new Error('Upload to S3 failed (' + putRes.status + ')');

            // Step 4: stamp the new key into the form. Save still required.
            setForm(prev => ({ ...prev, logoS3Key: s3Key, logoUrl: '' }));
        } catch (err: any) {
            setLogoUploadError(err?.message || 'Logo upload failed');
            setLogoLocalPreview(null);
        } finally {
            setLogoUploading(false);
        }
    };

    const clearLogo = () => {
        setForm(prev => ({ ...prev, logoS3Key: '', logoUrl: '' }));
        setLogoLocalPreview(null);
        setLogoUploadError(null);
    };

    useEffect(() => {
        let cancelled = false;
        brandingService.get()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data || EMPTY;
                setForm({ ...EMPTY, ...data });
                setSavedWhatsappNumber(data.whatsappNumber || '');
            })
            .catch(() => { /* fall back to empty defaults */ })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, []);

    const set = <K extends keyof BrandingProfile>(k: K, v: BrandingProfile[K]) =>
        setForm(prev => ({ ...prev, [k]: v }));

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await brandingService.update(form);
            const saved = res.data?.data || form;
            setForm({ ...EMPTY, ...saved });
            setSavedWhatsappNumber(saved.whatsappNumber || '');
            setSavedAt(new Date().toLocaleTimeString('en-GB'));
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to save branding');
        } finally {
            setSaving(false);
        }
    };

    const confirmWhatsapp = async () => {
        setConfirmingWa(true);
        setWaError(null);
        try {
            const res = await brandingService.confirmWhatsappNumber();
            setForm(prev => ({ ...prev, ...(res.data?.data || {}) }));
        } catch (e: any) {
            setWaError(e?.response?.data?.message || e?.message || 'Failed to confirm number');
        } finally {
            setConfirmingWa(false);
        }
    };

    // Live preview of the email header — what the tenant will see in their inbox.
    const previewName = form.fromName || form.orgName || 'Landlord Pro';
    const previewColor = form.brandColor || '#10b981';

    if (loading) {
        return <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
            <i className="fas fa-spinner fa-spin mr-2"></i>Loading branding…
        </div>;
    }

    return (
        <div className="space-y-6">

            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-5 border-b">
                    <h3 className="text-lg font-bold text-gray-900">Email Branding</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        How your organisation appears in emails sent to tenants. Sender envelope stays on the verified
                        Landlord Pro domain (so SPF/DKIM don't break), but the visible "From" name, reply-to and
                        footer come from here.
                    </p>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    <Field
                        label="Organisation Name *"
                        hint="Shown in the email header and as the default From name."
                        value={form.orgName}
                        onChange={v => set('orgName', v)}
                        placeholder="Causeway Property Services"
                    />
                    <Field
                        label="From Name"
                        hint='Appears as "Causeway Property Services <hello@compliance-engine.io>". Leave blank to use Org Name.'
                        value={form.fromName}
                        onChange={v => set('fromName', v)}
                        placeholder="Causeway Property Services"
                    />
                    <Field
                        label="Reply-To Email"
                        hint="Tenants' replies route here instead of the platform inbox."
                        value={form.replyToEmail || ''}
                        onChange={v => set('replyToEmail', v)}
                        placeholder="hello@causeway.example"
                        type="email"
                    />
                    <Field
                        label="Support Email"
                        hint="Shown in the email footer."
                        value={form.supportEmail || ''}
                        onChange={v => set('supportEmail', v)}
                        placeholder="support@causeway.example"
                        type="email"
                    />
                    <Field
                        label="Support Phone"
                        value={form.supportPhone || ''}
                        onChange={v => set('supportPhone', v)}
                        placeholder="020 1234 5678"
                    />
                    <Field
                        label="Website"
                        value={form.websiteUrl || ''}
                        onChange={v => set('websiteUrl', v)}
                        placeholder="https://causeway.example"
                    />
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                        <p className="text-xs text-gray-500 mb-2">PNG/JPEG/SVG/WebP, max 2 MB. Shown at the top of every email (max ~40px tall).</p>
                        {(logoLocalPreview || form.logoUrl || form.logoS3Key) ? (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                {(logoLocalPreview || form.logoUrl) ? (
                                    <img
                                        src={logoLocalPreview || form.logoUrl || ''}
                                        alt="Logo preview"
                                        style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }}
                                        className="bg-white border border-gray-200 rounded px-2"
                                    />
                                ) : (
                                    <div className="text-xs text-gray-500 italic">Uploaded — preview will appear after save & reload</div>
                                )}
                                <div className="flex gap-2 ml-auto">
                                    <label className={`text-xs font-medium text-emerald-700 hover:text-emerald-900 cursor-pointer ${logoUploading ? 'opacity-50 cursor-wait' : ''}`}>
                                        {logoUploading ? 'Uploading…' : 'Replace'}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                            disabled={logoUploading}
                                            className="hidden"
                                            onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoFile(f); e.target.value = ''; }}
                                        />
                                    </label>
                                    <button type="button" onClick={clearLogo} className="text-xs font-medium text-rose-600 hover:text-rose-800">
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <label className={`flex items-center justify-center gap-2 px-3 py-6 bg-gray-50 border border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 transition ${logoUploading ? 'opacity-60 cursor-wait' : ''}`}>
                                <i className={`fas ${logoUploading ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} text-gray-400`}></i>
                                <span className="text-sm font-medium text-gray-600">
                                    {logoUploading ? 'Uploading…' : 'Click to upload a logo'}
                                </span>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                    disabled={logoUploading}
                                    className="hidden"
                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoFile(f); e.target.value = ''; }}
                                />
                            </label>
                        )}
                        {logoUploadError && (
                            <p className="mt-1 text-xs text-rose-600">{logoUploadError}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Brand Colour</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                value={form.brandColor}
                                onChange={e => set('brandColor', e.target.value)}
                                className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                                type="text"
                                value={form.brandColor}
                                onChange={e => set('brandColor', e.target.value)}
                                className="flex-1 border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm font-mono focus:ring-green-500 focus:border-green-500"
                                placeholder="#006d3b"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Used for header accent and links.</p>
                    </div>
                    <div className="md:col-span-2">
                        <Field
                            label="Footer Text"
                            hint='Optional. e.g. "Causeway · Authorised letting agent of …"'
                            value={form.footerText}
                            onChange={v => set('footerText', v)}
                            placeholder=""
                        />
                    </div>
                </div>

                {error && (
                    <div className="mx-6 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
                        {error}
                    </div>
                )}

                <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                        {savedAt ? <span className="text-emerald-600 font-medium">Saved at {savedAt}</span> : 'Changes apply to the next email sent.'}
                    </div>
                    <button
                        onClick={save}
                        disabled={saving || !form.orgName.trim()}
                        className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Save Branding'}
                    </button>
                </div>
            </div>

            {/* WhatsApp hazard reporting number */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-5 border-b">
                    <h3 className="text-lg font-bold text-gray-900">
                        <i className="fab fa-whatsapp text-green-500 mr-2"></i>WhatsApp Hazard Reporting
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        The number you tell tenants to message when reporting a hazard (damp, mould, a broken
                        boiler, anything else) with a photo. This is shown to tenants as your contact number —
                        it doesn't change how messages are received. Every tenant's own phone number, once saved
                        on their record, is automatically matched to the right hazard case the moment they message
                        in, no separate setup needed per tenant.
                    </p>
                </div>
                <div className="p-6 space-y-4">
                    <div className="max-w-sm">
                        <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp number</label>
                        <input
                            type="text"
                            value={form.whatsappNumber || ''}
                            onChange={e => set('whatsappNumber', e.target.value)}
                            placeholder="+44 7700 900123"
                            className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use the full international format, e.g. +44 7700 900123.</p>
                    </div>

                    <div className="flex items-center gap-3">
                        {form.whatsappConfirmed ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                                <i className="fas fa-circle-check"></i>
                                Confirmed{form.whatsappConfirmedAt ? ` ${new Date(form.whatsappConfirmedAt).toLocaleString('en-GB')}` : ''}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                                <i className="fas fa-triangle-exclamation"></i>
                                Not yet confirmed
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={confirmWhatsapp}
                            disabled={confirmingWa || !form.whatsappNumber?.trim() || (form.whatsappNumber || '') !== savedWhatsappNumber}
                            title={(form.whatsappNumber || '') !== savedWhatsappNumber ? 'Save your change to this number first' : undefined}
                            className="text-xs font-semibold text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 rounded-lg px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {confirmingWa ? 'Confirming…' : 'Confirm this is correct'}
                        </button>
                    </div>
                    {(form.whatsappNumber || '') !== savedWhatsappNumber && (
                        <p className="text-xs text-amber-600">
                            <i className="fas fa-circle-info mr-1"></i>Save this number below before you can confirm it.
                        </p>
                    )}
                    <p className="text-xs text-gray-400">
                        Changing the number clears any earlier confirmation, so "Confirmed" always reflects a
                        deliberate check of the current number, not a stale save.
                    </p>
                    {waError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{waError}</div>
                    )}
                </div>
            </div>

            {/* Live preview */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Live Preview</h3>
                    <p className="text-xs text-gray-500 mt-1">What your tenants will see in their inbox.</p>
                </div>
                <div className="p-6 bg-gray-100">
                    <div className="max-w-xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-5 py-3 border-b bg-gray-50 text-xs">
                            <div><span className="font-bold text-gray-500">From:</span> {previewName} &lt;hello@compliance-engine.io&gt;</div>
                            {form.replyToEmail && <div><span className="font-bold text-gray-500">Reply-To:</span> {form.replyToEmail}</div>}
                            <div><span className="font-bold text-gray-500">Subject:</span> Message from {previewName}</div>
                        </div>
                        <div className="p-6" style={{ fontFamily: 'sans-serif', lineHeight: 1.5, color: '#374151' }}>
                            {(logoLocalPreview || form.logoUrl)
                                ? <img src={logoLocalPreview || form.logoUrl || ''} alt={previewName} style={{ maxHeight: 40, marginBottom: 16 }} />
                                : <div style={{ fontWeight: 700, color: previewColor, fontSize: 18, marginBottom: 16 }}>{previewName}</div>
                            }
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                Hi [Tenant Name],{'\n\n'}This is an example of how your messages will appear.
                            </div>
                            <hr style={{ marginTop: 24, border: 'none', borderTop: '1px solid #E5E7EB' }} />
                            <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 12 }}>
                                {form.footerText || `Sent via ${previewName}`}
                            </p>
                            {(form.supportEmail || form.supportPhone || form.websiteUrl) && (
                                <p style={{ fontSize: 12, color: '#9CA3AF' }}>
                                    {[
                                        form.supportEmail && <a key="e" href={`mailto:${form.supportEmail}`} style={{ color: previewColor }}>{form.supportEmail}</a>,
                                        form.supportPhone,
                                        form.websiteUrl && <a key="w" href={form.websiteUrl} style={{ color: previewColor }}>{form.websiteUrl}</a>,
                                    ].filter(Boolean).reduce((acc: any, item, i, arr) => acc.concat(i < arr.length - 1 ? [item, ' · '] : [item]), [])}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({
    label, hint, value, onChange, placeholder, type = 'text',
}: {
    label: string;
    hint?: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
            />
            {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
    );
}
